import { useEffect, useState } from 'react';
import {
  Avatar, Checkbox,
  Dialog, InputLabel,
} from '@mui/material';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { useSnackbar } from 'notistack';
import { Role } from 'lib/types';
import { AutoCredit } from 'lib/types/postingAccounts';
import { User } from 'lib/types/users';
import { useApi } from 'lib/contexts/ApplicationState';
import { RolesProvider } from 'lib/contexts/rolesContext';
import { RoleSelect } from 'components/RoleSelect';
import { NETWORKS } from 'lib/constants';
import { imageAvatarUrl } from 'lib/utils/imageUtils';

interface Props {
  postingAccountId: number;
  network: number;
  username: string;
  user?: User | null;
  autoCredit?: AutoCredit;
  onAdd: (arg?: AutoCredit) => void;
  onClose: () => void;
  showCreditedUser?: boolean;
}

export const AutoCreditModal = ({
  postingAccountId, autoCredit, onAdd, onClose, network, username, user, showCreditedUser = false,
}: Props) => {
  const API = useApi();
  const { enqueueSnackbar } = useSnackbar();
  const [autoCreditsCount, setAutoCreditsCount] = useState(0);

  const [_role, setRole] = useState<Role|null>(
    autoCredit?.role || user?.defaultRole || null,
  );

  const [autoCreditExistingPosts, setAutoCreditExistingPosts] = useState(false);

  useEffect(() => {
    /**
     * Determine how many existing posts would be updated if auto-credits are enabled for
     * this postingAccountId. We can hide the checkbox at the bottom of the dialog if it
     * is 0.
     */
    const fetchAutoCreditsCount = async () => {
      try {
        const response = await API.countAutoCreditsToCreate(postingAccountId);
        setAutoCreditsCount(response?.count);
      } catch (err) {
        setAutoCreditsCount(0);
      }
    };
    if (API && user?.id) {
      fetchAutoCreditsCount();
    }
  }, [API, user?.id, postingAccountId]);

  const add = async () => {
    if (!_role?.id || !user?.id) return;

    const ac: AutoCredit = {
      ...autoCredit,
      postingAccountId,
      userId: user?.id,
      roleId: _role.id,
    };
    try {
      const newAutoCredit = await API.upsertAutoCredit(ac, autoCreditExistingPosts);
      enqueueSnackbar('Auto crediting has been updated', {
        variant: 'success',
      });
      onAdd({ ...newAutoCredit, role: _role });
    } catch (e: any) {
      enqueueSnackbar(`An error occurred: ${e.message}`, {
        variant: 'error',
      });
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
    >
      <div className="p-4 md:min-w-[480px]">
        <h1 className="text-xl font-semibold mb-2">Automatically add credits?</h1>
        <div className="absolute right-2 top-2">
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </div>
        <p className="mb-2">
          We will automatically add a credit with the following role
          to any new
          {' '}
          <span className="font-semibold">

            {' '}
            {NETWORKS[network].title}
          </span>
          {' '}
          posts from
          {' '}
          <span className="font-semibold">
            @
            {username}
          </span>
          .
        </p>
        {(showCreditedUser && user) && (
          <div>
            User to credit:
            <div className="my-3 flex underline">
              <Avatar
                className="mr-1"
                sx={{ width: 25, height: 25 }}
                key={user?.id}
                variant="circular"
                src={imageAvatarUrl(
                  user.avatarUrl,
                )}
                alt={user?.name}
                imgProps={{ loading: 'lazy' }}
              />
              <a href={`/${user.username}?preview=1`}>
                {user?.name}
              </a>
            </div>
          </div>
        )}
        <p className="mb-4">Please select a role that is the most relevant to the account.</p>
        <div className="mb-4">
          <InputLabel>Role</InputLabel>
          <RolesProvider>
            <RoleSelect onChange={(r) => setRole(r as (Role | null))} value={_role} />
          </RolesProvider>
        </div>
        {!!autoCreditsCount
          && (
            <div className="my-4 flex items-start">
              <Checkbox
                className="align-top py-0 pl-0 pr-2"
                checked={autoCreditExistingPosts}
                onChange={() => setAutoCreditExistingPosts(!autoCreditExistingPosts)}
              />
              <span className="align-top">
                Also use this role to update
                {' '}
                <span className="font-semibold">
                  ~
                  {autoCreditsCount}
                </span>
                {' '}
                pre-existing
                {' '}
                <span className="font-semibold">
                  @
                  {username}
                </span>
                {' '}
                {NETWORKS[network].title}

                {' '}
                posts without credits
              </span>
            </div>
          )}
        <div className="flex justify-end mt-5">
          <button
            className="btn-primary-outlined mr-2"
            onClick={onClose}
            type="button"
          >
            Cancel
          </button>

          <button
            className="btn-primary"
            onClick={add}
            type="submit"
            disabled={!_role}
          >
            Save
          </button>
        </div>
      </div>
    </Dialog>
  );
};
